/**
 *  @class
 *  @function ThemeFooter
 *
 * Desktop footer "reveal" clips #main-content so the dark footer shows at the sides.
 * That fights infinite/load-more collection pagination (sentinel sits on top of the
 * footer), so the reveal stays disabled until pagination is exhausted.
 */

if (!customElements.get('theme-footer')) {
  class ThemeFooter extends HTMLElement {
    constructor() {
      super();
      this.revealTween = null;
      this.mm = null;
    }
    connectedCallback() {
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';

      if (document.body.classList.contains('template-product-quick-view')) {
        this.animations_enabled = false;
      }
      if (!this.animations_enabled) {
        return;
      }
      this.content = document.getElementById('main-content');
      this.wrapper = document.getElementById('wrapper');
      this.footer_bg = window.getComputedStyle(document.documentElement).getPropertyValue('--color-footer-bg');
      this.radius = window.getComputedStyle(document.documentElement).getPropertyValue('--block-border-radius');
      this.wrapper.style.backgroundColor = this.footer_bg;
      this.bindListeners();
      this.syncRevealWithPagination();
    }
    hasAjaxPagination() {
      return !!document.querySelector(
        'pagination-theme.pagination-type--infinite, pagination-theme.pagination-type--loadmore'
      );
    }
    resetRevealVars() {
      if (!this.content) {
        return;
      }
      this.content.style.setProperty('--footer-width-percent', '0%');
      this.content.style.setProperty('--footer-border-radius', '0px');
    }
    killReveal() {
      if (this.revealTween) {
        if (this.revealTween.scrollTrigger) {
          this.revealTween.scrollTrigger.kill();
        }
        this.revealTween.kill();
        this.revealTween = null;
      }
      if (this.mm) {
        this.mm.revert();
        this.mm = null;
      }
      this.resetRevealVars();
    }
    syncRevealWithPagination() {
      if (this.hasAjaxPagination()) {
        this.killReveal();
        return;
      }
      if (!this.revealTween) {
        this.setupAnimations();
      } else if (typeof ScrollTrigger !== 'undefined') {
        requestAnimationFrame(() => ScrollTrigger.refresh());
      }
    }
    bindListeners() {
      // Still loading more pages — keep side reveal off and clear any partial clip.
      document.addEventListener('pagination:page-changed', () => {
        this.killReveal();
      });
      // Last page reached — safe to enable the footer side reveal.
      document.addEventListener('pagination:exhausted', () => {
        this.syncRevealWithPagination();
      });
      // Facet/filter swaps can add or remove ajax pagination.
      document.addEventListener('facets:rendered', () => {
        this.syncRevealWithPagination();
      });
    }
    setupAnimations() {
      if (this.hasAjaxPagination() || this.revealTween) {
        return;
      }

      this.mm = gsap.matchMedia();
      this.mm.add({
        isDesktop: `(min-width: 768px)`,
        reduceMotion: "(prefers-reduced-motion: reduce)"
      }, (context) => {
        let {
          isDesktop,
          reduceMotion
        } = context.conditions;

        if (!isDesktop || reduceMotion) {
          this.resetRevealVars();
          return;
        }

        this.revealTween = gsap.to(this.content, {
          "--footer-border-radius": `${this.radius}`,
          "--footer-width-percent": '4%',
          duration: 0.5,
          inherit: false,
          ease: "none",
          modifiers: {
            "--footer-border-radius": x => Math.round(parseFloat(x)) + "px",
            "--footer-width-percent": x => Math.round(parseFloat(x) * 100) / 100 + "%"
          },
          scrollTrigger: {
            trigger: this,
            fastScrollEnd: true,
            scrub: 1,
            invalidateOnRefresh: true,
            start: () => `top bottom`,
            end: () => `bottom bottom`
          }
        });
      });
    }
  }
  customElements.define('theme-footer', ThemeFooter);
}
